if (!customElements.get("cart-notification")) {
	class CartNotification extends HTMLElement {
		constructor() {
			super();
			this.notification = document.getElementById("cart-notification");
			this.header = document.querySelector("sticky-header");
			this.onBodyClick = this.handleBodyClick.bind(this);
			this.closeTimeout = null;
			this.NOTIFICATION_TIMEOUT = 5000; // 5 seconds timeout

			if (!this.notification) {
				return;
			}

			this.notification.addEventListener(
				"keyup",
				(evt) => evt.code === "Escape" && this.close()
			);
			this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
				closeButton.addEventListener("click", this.close.bind(this))
			);

			// Add event listener for the continue shopping button
			const continueShoppingButton = this.querySelector(
				"#cart-notification-continue-shopping"
			);
			if (continueShoppingButton) {
				continueShoppingButton.addEventListener("click", this.close.bind(this));
			}

			if (typeof window.subscribe === "function") {
				window.subscribe("cart-update", (data) => {
					if (window.location.pathname.includes("/cart")) {
						return;
					}

					if (data && data.cartData) {
						// For product forms, we need to ensure we have proper item data
						// We need to extract the item details from the cartData response
						const cartWithItems = this.ensureCartItemsData(data.cartData);

						this.fetchCartSections(cartWithItems)
							.then((cartWithSections) => {
								const notificationData = {
									key:
										data.key ||
										data.variantId || // Include productVariantId as fallback key
										(cartWithSections.items && cartWithSections.items[0]?.key),
									items: cartWithSections.items || [],
									sections: cartWithSections.sections || {},
								};

								if (notificationData.items.length > 0) {
									this.renderContents(notificationData);
								}
							})
							.catch((error) => {
								console.error("🔔 [CART] Error fetching cart sections:", error);
							});
					}
				});
			}
		}

		// Ensure we have proper items data from cart response
		ensureCartItemsData(cartData) {
			if (!cartData.items && cartData.item_count > 0) {
				// If we're missing item details but have a count, we need to fetch the full cart
				console.log("🔔 [CART] Fetching complete cart data");

				// Return cart data as is - we'll fetch sections which will include necessary data
				return cartData;
			}
			return cartData;
		}

		fetchCartSections(cart) {
			// Don't try to fetch sections on product pages or collection pages
			// Just return the original cart object
			if (
				window.location.pathname.includes("/products/") ||
				window.location.pathname.includes("/collections/")
			) {
				return Promise.resolve(cart);
			}

			// Make multiple section requests to ensure we have all the necessary data
			const sectionIds = ["cart-notification", "cart-icon-bubble"];

			// Get the shop domain - making sure we have a complete, valid URL
			const shopDomain = window.location.origin;

			return Promise.all(
				sectionIds.map((sectionId) =>
					fetch(`${shopDomain}?section_id=${sectionId}`)
						.then((response) => {
							if (!response.ok) {
								throw new Error(
									`Failed to fetch section ${sectionId}: ${response.status}`
								);
							}
							return response.text();
						})
						.then((text) => {
							const div = document.createElement("div");
							div.innerHTML = text;
							return { sectionId, html: div };
						})
						.catch((error) => {
							// Don't log errors for expected 404s on product pages
							if (!window.location.pathname.includes("/products/")) {
								console.error(
									`🔔 [CART] Error fetching section ${sectionId}:`,
									error
								);
							}
							return { sectionId, html: document.createElement("div") };
						})
				)
			).then((results) => {
				// Process the section data and add it to the cart object
				cart.sections = cart.sections || {};

				// Merge all sections into cart.sections
				results.forEach((result) => {
					const { sectionId, html } = result;

					if (sectionId === "cart-notification") {
						cart.sections["cart-notification-product"] =
							html.querySelector("#cart-notification-product")?.outerHTML || "";

						cart.sections["cart-notification-button"] =
							html.querySelector("#cart-notification-button")?.outerHTML || "";
					}

					if (sectionId === "cart-icon-bubble") {
						cart.sections["cart-icon-bubble"] =
							html.querySelector("#cart-icon-bubble")?.outerHTML || "";
					}
				});

				return cart;
			});
		}

		open() {
			// Clear any existing timeout
			if (this.closeTimeout) {
				clearTimeout(this.closeTimeout);
				this.closeTimeout = null;
			}

			this.notification.classList.add("animate", "active");
			this.notification.addEventListener(
				"transitionend",
				() => {
					this.notification.focus();
					if (typeof trapFocus === "function") {
						trapFocus(this.notification);
					}
				},
				{ once: true }
			);
			document.body.addEventListener("click", this.onBodyClick);

			// Set timeout to close notification after specified time
			this.closeTimeout = setTimeout(() => {
				this.close();
			}, this.NOTIFICATION_TIMEOUT);
		}

		close() {
			// Clear any existing timeout
			if (this.closeTimeout) {
				clearTimeout(this.closeTimeout);
				this.closeTimeout = null;
			}

			this.notification.classList.remove("active");
			document.body.removeEventListener("click", this.onBodyClick);
			if (typeof removeTrapFocus === "function") {
				removeTrapFocus(this.activeElement);
			}
		}

		renderContents(parsedState) {
			if (!this.notification) {
				console.error(
					"🔔 [CART] Cannot render contents - notification element not found"
				);
				return;
			}

			this.cartItemKey = parsedState.key;

			try {
				// Store the sections data for later use
				if (parsedState.sections) {
					this.lastReceivedSections = parsedState.sections;
				}

				// First, check if we have a valid items array with data
				if (
					!parsedState.items ||
					!Array.isArray(parsedState.items) ||
					parsedState.items.length === 0
				) {
					// If we don't have items but we do have a key (from product form), try to get item info from cart
					if (parsedState.key) {
						// Fetch current cart to get item data
						fetch("/cart.js")
							.then((response) => response.json())
							.then((cart) => {
								// Find the item that matches our key (usually variant ID on product forms)
								let foundItem = null;
								if (cart.items && cart.items.length) {
									// Match by variant ID (key) if it's a product form submission
									foundItem = cart.items.find((item) => {
										return (
											item.variant_id === parseInt(parsedState.key) ||
											item.key === parsedState.key
										);
									});

									// If we found the item, rerun renderContents with the full item data
									if (foundItem) {
										parsedState.items = cart.items;
										this.renderLatestAddedProduct(foundItem);
										// Calculate and display cart subtotal
										this.updateCartSubtotal(cart.items);
										// Update the view cart button
										this.updateViewCartButton(
											cart.items.reduce(
												(total, item) => total + item.quantity,
												0
											)
										);
										// Update free gift selector
										this.updateFreeGiftSelector(cart.items);
										if (this.header) this.header.reveal();
										this.open();
										return;
									}
								}

								// If we couldn't find the item, show a basic notification
								this.showBasicNotification();
							})
							.catch((error) => {
								console.error("🔔 [CART] Error fetching cart data:", error);
								this.showBasicNotification();
							});
						return;
					} else {
						// If we have no items and no key, show a basic notification
						this.showBasicNotification();
						return;
					}
				}

				// Update cart icon bubble first (common element)
				const totalItemCount = this.updateCartIconBubble(parsedState.items);

				// Create product display manually for immediate display
				// This ensures the product image is always shown regardless of section data
				if (parsedState.items && parsedState.items.length > 0) {
					// Find the most recently added item
					const latestItem = this.findLatestAddedItem(parsedState.items);
					if (latestItem) {
						this.renderLatestAddedProduct(latestItem);
					}
				}

				// Still use sections for other parts if available
				if (parsedState.sections) {
					this.getSectionsToRender().forEach((section) => {
						// Skip product section since we're handling it manually
						if (section.id === "cart-notification-product") {
							return;
						}

						const sectionElement = document.getElementById(section.id);
						if (sectionElement && parsedState.sections[section.id]) {
							sectionElement.innerHTML = this.getSectionInnerHTML(
								parsedState.sections[section.id],
								section.selector
							);
						} else {
							// Only log warnings in development mode or if it's not one of our known missing sections
							if (
								(window.Shopify &&
									window.Shopify.environment === "development") ||
								(section.id !== "cart-notification-button" &&
									section.id !== "cart-icon-bubble")
							) {
								console.warn(
									`🔔 [CART] Missing section ${section.id} or section data`
								);
							}
						}
					});
				}

				// Calculate and display cart subtotal
				this.updateCartSubtotal(parsedState.items);

				// Update view cart button text based on item count
				this.updateViewCartButton(totalItemCount);

				// Update free gift selector
				this.updateFreeGiftSelector(parsedState.items);

				if (this.header) this.header.reveal();
				this.open();
			} catch (error) {
				console.error("🔔 [CART] Error rendering contents:", error);
				console.error(error);
				this.showBasicNotification();
			}
		}

		// Find the most recently added item (usually the one with key matching cartItemKey)
		findLatestAddedItem(items) {
			if (!items || !items.length) return null;

			// First try to match by key
			if (this.cartItemKey) {
				const foundItem = items.find((item) => item.key === this.cartItemKey);
				if (foundItem) return foundItem;
			}

			// If no match or no cartItemKey, return the first item
			return items[0];
		}

		// Manually render the product display for immediate visual feedback
		renderLatestAddedProduct(item) {
			const productContainer = document.getElementById(
				"cart-notification-product"
			);
			if (!productContainer) return;

			// Ensure we have an image URL with comprehensive fallbacks
			let productImage = "";

			// Try different image paths based on the structure we received
			if (item.featured_image && item.featured_image.url) {
				productImage = item.featured_image.url;
			} else if (item.image) {
				productImage =
					typeof item.image === "string" ? item.image : item.image.src || "";
			} else if (
				item.product_has_only_default_variant &&
				item.product_featured_image
			) {
				productImage = item.product_featured_image;
			} else if (item.featured_media && item.featured_media.preview_image) {
				productImage = item.featured_media.preview_image.src;
			}

			// If we still don't have an image, try a final fallback for product page adds
			if (!productImage && item.url) {
				const productUrlParts = item.url.split("?")[0].split("/");
				const productHandle = productUrlParts[productUrlParts.length - 1];
				productImage = `/products/${productHandle}/featured_image`;
			}

			const productTitle = item.product_title || item.title || "Product";
			const productVariantTitle = item.variant_title || "";
			const productQuantity = item.quantity || 1;

			let variantInfo = "";
			if (productVariantTitle && productVariantTitle !== "Default Title") {
				variantInfo = `<div class="cart-notification-product__variant">${productVariantTitle}</div>`;
			}

			const priceDisplay = this.formatMoney(item.final_line_price);

			productContainer.innerHTML = `
				<div class="cart-item">
					${
						productImage
							? `
						<div class="cart-notification-product__image global-media-settings">
							<img src="${productImage}" alt="${productTitle}" width="70" height="70" loading="lazy">
						</div>
					`
							: ""
					}
					<div class="cart-notification-product__info">
						<h3 class="cart-notification-product__name h4">${productTitle}</h3>
						${variantInfo}
						<div class="cart-notification-product__price">
							<span class="cart-notification-product__quantity">${productQuantity}×</span>
							<span class="cart-notification-product__price-value">${priceDisplay}</span>
						</div>
					</div>
				</div>
			`;
		}

		// Update cart icon bubble (cart count) and return the total quantity
		updateCartIconBubble(items) {
			if (!items) return 0;

			const cartIconBubble = document.getElementById("cart-icon-bubble");
			if (!cartIconBubble) return 0;

			// Calculate total quantity across all items
			const totalQuantity = items.reduce(
				(total, item) => total + item.quantity,
				0
			);

			const cartCountBubble =
				cartIconBubble.querySelector(".cart-count-bubble");

			if (totalQuantity === 0) {
				if (cartCountBubble) cartCountBubble.remove();
			} else {
				if (!cartCountBubble) {
					const newBubble = document.createElement("div");
					newBubble.classList.add("cart-count-bubble");
					cartIconBubble.appendChild(newBubble);
				}

				const countBubble = cartIconBubble.querySelector(".cart-count-bubble");
				if (countBubble) {
					countBubble.innerHTML = `
						${totalQuantity < 100 ? `<span aria-hidden="true">${totalQuantity}</span>` : ""}
						<span class="visually-hidden">Cart count: ${totalQuantity}</span>
					`;
				}
			}

			// Return the total quantity for use elsewhere
			return totalQuantity;
		}

		formatMoney(cents) {
			const moneyFormat = "{{ shop.money_format }}";

			if (typeof cents === "string") {
				cents = cents.replace(".", "");
			}

			const value = cents / 100;
			return value.toFixed(2) + " kr";
		}

		getSectionsToRender() {
			return [
				{
					id: "cart-notification-product",
					selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
				},
				{ id: "cart-notification-button" },
				{ id: "cart-icon-bubble" },
			];
		}

		getSectionInnerHTML(html, selector = ".shopify-section") {
			try {
				return new DOMParser()
					.parseFromString(html, "text/html")
					.querySelector(selector).innerHTML;
			} catch (error) {
				console.error("🔔 [CART] Error parsing section HTML:", error);
				return "";
			}
		}

		handleBodyClick(evt) {
			const target = evt.target;
			if (
				target !== this.notification &&
				!target.closest("cart-notification")
			) {
				const disclosure = target.closest("details-disclosure, header-menu");
				this.activeElement = disclosure
					? disclosure.querySelector("summary")
					: null;
				this.close();
			}
		}

		setActiveElement(element) {
			this.activeElement = element;
		}

		// Update free gift selector by using the sections data or fetching the updated snippet
		updateFreeGiftSelector(items) {
			const giftSelectorContainer = document.getElementById(
				"cart-notification-gift-selector"
			);
			if (!giftSelectorContainer) return;

			// Try to use the sections data first if available
			if (
				this.lastReceivedSections &&
				this.lastReceivedSections["free-gift-selector"]
			) {
				giftSelectorContainer.innerHTML =
					this.lastReceivedSections["free-gift-selector"];
				return;
			}

			// Generate the free gift progress bar directly without fetching
			this.createSimplifiedGiftSelector(giftSelectorContainer, items);

			// Don't try to fetch the free gift section - it doesn't work and causes 404 errors
			// Just use our generated version instead
		}

		// Create a simplified gift selector without requiring section fetch
		createSimplifiedGiftSelector(container, items) {
			if (!container) return;

			// Calculate cart total
			const cartTotal = Array.isArray(items)
				? items.reduce((total, item) => {
						// Exclude free gift items from the total calculation
						if (item.properties && item.properties._free_gift) {
							return total;
						}
						return total + item.final_line_price;
				  }, 0)
				: 0;

			// Get threshold values from theme settings (fallback to defaults if not available)
			const giftThreshold =
				window.theme && window.theme.freeGiftThreshold
					? parseFloat(window.theme.freeGiftThreshold)
					: 299;
			const shippingThreshold =
				window.theme && window.theme.freeShippingThreshold
					? parseFloat(window.theme.freeShippingThreshold)
					: 399;

			const giftThresholdInCents = giftThreshold * 100;
			const shippingThresholdInCents = shippingThreshold * 100;

			// Determine if thresholds are met
			const giftThresholdMet = cartTotal >= giftThresholdInCents;
			const shippingThresholdMet = cartTotal >= shippingThresholdInCents;

			const amountLeft = giftThresholdInCents - cartTotal;
			const formattedAmountLeft = this.formatMoney(Math.max(amountLeft, 0));

			let progressPercentage,
				giftMarkerPosition,
				shippingMarkerPosition,
				giftLabelPosition,
				shippingLabelPosition;
			let giftStyleOverride = "text-align: center;";
			let shippingStyleOverride = "text-align: center;";

			const bothThresholdsActive = giftThreshold > 0 && shippingThreshold > 0;

			if (bothThresholdsActive && giftThreshold !== shippingThreshold) {
				// Spaced-out visual logic
				const firstThresholdInCents = Math.min(
					giftThresholdInCents,
					shippingThresholdInCents
				);
				const secondThresholdInCents = Math.max(
					giftThresholdInCents,
					shippingThresholdInCents
				);

				const firstMarkerPos = 50.0;
				const secondMarkerPos = 100.0;
				const firstLabelPos = 50.0;
				const secondLabelPos = 87.0;

				if (cartTotal < firstThresholdInCents) {
					progressPercentage =
						firstThresholdInCents > 0
							? (cartTotal / firstThresholdInCents) * firstMarkerPos
							: 0;
				} else {
					const progressInSecondRange = cartTotal - firstThresholdInCents;
					const totalSecondRange =
						secondThresholdInCents - firstThresholdInCents;
					if (totalSecondRange > 0) {
						const percentageInSecondRange =
							progressInSecondRange / totalSecondRange;
						const progressInSecondSegment =
							percentageInSecondRange * (secondMarkerPos - firstMarkerPos);
						progressPercentage = firstMarkerPos + progressInSecondSegment;
					} else {
						progressPercentage = firstMarkerPos;
					}
				}
				progressPercentage = Math.min(progressPercentage, 100);

				if (giftThresholdInCents < shippingThresholdInCents) {
					giftMarkerPosition = firstMarkerPos;
					shippingMarkerPosition = secondMarkerPos;
					giftLabelPosition = firstLabelPos;
					shippingLabelPosition = secondLabelPos;
					shippingStyleOverride = "text-align: right; width: 100px;";
				} else {
					shippingMarkerPosition = firstMarkerPos;
					giftMarkerPosition = secondMarkerPos;
					shippingLabelPosition = firstLabelPos;
					giftLabelPosition = secondLabelPos;
					giftStyleOverride = "text-align: right; width: 100px;";
				}
			} else {
				// Original linear logic
				const maxThreshold = Math.max(
					giftThresholdInCents,
					shippingThresholdInCents
				);
				progressPercentage =
					maxThreshold > 0
						? Math.min((cartTotal / maxThreshold) * 100, 100)
						: 0;

				giftMarkerPosition =
					maxThreshold > 0 ? (giftThresholdInCents / maxThreshold) * 100 : 0;
				shippingMarkerPosition =
					maxThreshold > 0
						? (shippingThresholdInCents / maxThreshold) * 100
						: 0;
				giftLabelPosition = giftMarkerPosition;
				shippingLabelPosition = shippingMarkerPosition;
			}

			// Create a style element to add the necessary CSS
			const styleEl = document.createElement("style");
			styleEl.textContent = `
				#cart-notification-gift-selector .free-gift-progress-container {
					margin: 1.5rem 0 0 0;
					max-width: 100%;
					position: relative;
					padding-bottom: 2.5rem;
				}
				
				#cart-notification-gift-selector .free-gift-progress-bar {
					height: 8px;
					background-color: #f0f0f0;
					border-radius: 4px;
					position: relative;
					margin-bottom: 2rem;
					width: 100%;
				}
				
				#cart-notification-gift-selector .free-gift-progress-fill {
					height: 100%;
					background-color: rgba(72, 187, 120, 1);
					border-radius: 4px;
					transition: width 0.3s ease;
					position: absolute;
					top: 0;
					left: 0;
					display: block !important;
				}
				
				#cart-notification-gift-selector .free-gift-marker {
					position: absolute;
					top: -6px;
					width: 20px;
					height: 20px;
					border-radius: 50%;
					background-color: #fff;
					border: 2px solid #ccc;
					transform: translateX(-50%);
					z-index: 1;
					display: block !important;
				}
				
				#cart-notification-gift-selector .free-gift-marker.active {
					border-color: rgba(72, 187, 120, 1);
					background-color: rgba(72, 187, 120, 1);
				}
				
				#cart-notification-gift-selector .free-gift-labels {
					display: flex;
					justify-content: space-between;
					font-size: 0.75rem;
					color: #666;
					margin-top: 0.5rem;
					position: relative;
					width: 100%;
					min-height: 2.5rem;
				}
				
				#cart-notification-gift-selector .free-gift-labels span {
					text-align: center;
					white-space: nowrap;
					line-height: 1.2;
					font-size: 0.75rem;
					max-width: 80px;
				}
				
				#cart-notification-gift-selector .free-gift-labels span:first-child {
					text-align: left;
					transform: none !important;
					left: 0 !important;
					position: relative !important;
				}
				
				/* Hide all message and gift selection components */
				#cart-notification-gift-selector .free-gift-message,
				#cart-notification-gift-selector .free-gift-button,
				#cart-notification-gift-selector .choose-gift-section,
				#cart-notification-gift-selector .free-gift-selected,
				#cart-notification-gift-selector #free-gift-container {
					display: none !important;
				}

                /* Show/hide mobile/desktop containers */
                #cart-notification-gift-selector .desktop-free-gift-container { display: block; }
                #cart-notification-gift-selector .mobile-only { display: none; }

                @media (max-width: 1023px) {
                    #cart-notification-gift-selector .desktop-free-gift-container { display: none; }
                    #cart-notification-gift-selector .mobile-only { display: block; }
                }
			`;

			container.innerHTML = "";
			container.appendChild(styleEl);

			const generateProgressBarHTML = (containerClass) => {
				return `
            <div class="free-gift-progress-container ${containerClass}">
                <div class="free-gift-progress-bar">
                    <div class="free-gift-marker free-gift-marker-initial active" style="left: 0;"></div>
                    <div class="free-gift-progress-fill" style="width: ${progressPercentage}%;"></div>
                    <div class="free-gift-marker free-gift-marker-gift ${
											giftThresholdMet ? "active" : ""
										}" style="left: ${giftMarkerPosition}%;"></div>
                    <div class="free-gift-marker free-gift-marker-shipping ${
											shippingThresholdMet ? "active" : ""
										}" style="left: ${shippingMarkerPosition}%;"></div>
                </div>
                <div class="free-gift-labels">
                    <span>0</span>
                    <span style="position: absolute; left: ${giftLabelPosition}%; transform: translateX(-50%); ${giftStyleOverride}">
                        ${giftThreshold} kr<br>Gratis gave
                    </span>
                    <span style="position: absolute; left: ${shippingLabelPosition}%; transform: translateX(-50%); ${shippingStyleOverride}">
                        ${shippingThreshold} kr<br>Fri fragt
                    </span>
                </div>
            </div>
        `;
			};

			const content = document.createElement("div");
			content.innerHTML = `
        ${generateProgressBarHTML("desktop-free-gift-container")}
        ${generateProgressBarHTML("mobile-only")}
				<!-- Message container (hidden by CSS) -->
				<div class="free-gift-message">
					${
						giftThresholdMet
							? `<p>Du kvalificerer til en gratis gave!</p>
						<div class="choose-gift-section">
							<a href="/cart" class="free-gift-button">Vælg Gave</a>
						</div>`
							: `<p>Tilføj ${formattedAmountLeft} mere til kurven og få en gratis gave!</p>`
					}
				</div>
			`;

			container.appendChild(content);
		}

		// Helper method to update cart subtotal
		updateCartSubtotal(items) {
			if (!items || !Array.isArray(items) || items.length === 0) return;

			const subtotal = items.reduce(
				(total, item) => total + item.final_line_price,
				0
			);

			const subtotalElement = document.getElementById(
				"cart-notification-subtotal-value"
			);
			if (subtotalElement) {
				const formattedSubtotal = this.formatMoney(subtotal);
				subtotalElement.textContent = formattedSubtotal;
			}
		}

		// Helper method to update view cart button
		updateViewCartButton(totalItemCount) {
			const viewCartButton = document.getElementById(
				"cart-notification-button"
			);

			if (viewCartButton) {
				// Use total item count (quantity) instead of product count
				viewCartButton.textContent = `${
					viewCartButton.dataset.viewCartText || "View cart"
				} (${totalItemCount})`;
			}
		}

		// Show a basic notification when we can't get detailed item data
		showBasicNotification() {
			console.log("🔔 [CART] Showing basic notification");

			// Update free gift selector
			this.updateFreeGiftSelector([]);

			// Set basic content for the notification
			const productContainer = document.getElementById(
				"cart-notification-product"
			);
			if (productContainer) {
				productContainer.innerHTML = `
					<div class="cart-item">
						<div class="cart-notification-product__info">
							<h3 class="cart-notification-product__name h4">Item added to cart</h3>
						</div>
					</div>
				`;
			}

			// Set basic subtotal
			const subtotalElement = document.getElementById(
				"cart-notification-subtotal-value"
			);
			if (subtotalElement) {
				// Get the cart count from the cart icon if available
				const cartCount =
					document.querySelector(".cart-count-bubble span")?.textContent || "0";
				subtotalElement.textContent = "View cart for details";
			}

			// Update view cart button
			const viewCartButton = document.getElementById(
				"cart-notification-button"
			);
			if (viewCartButton) {
				viewCartButton.textContent =
					viewCartButton.dataset.viewCartText || "View cart";
			}

			if (this.header) this.header.reveal();
			this.open();
		}
	}
	customElements.define("cart-notification", CartNotification);
}
